import { useCallback, useEffect, useState } from 'react'

import { useClinic } from '~contexts/clinic-context'
import {
    CLINIC_MASCOT_UPDATED_EVENT,
    loadClinicMascot,
    saveClinicMascot,
    type ClinicMascotUpdatedDetail,
} from './mascot-storage'
import {
    DEFAULT_CLINIC_MASCOT,
    type ClinicMascotIdentity,
} from './mascot-types'

/**
 * Per-clinic mascot identity from localStorage.
 *
 * Every mounted instance reloads when any instance saves (same-tab custom
 * event) or another tab writes (native `storage` event), so the header
 * trigger, dock, and editor never drift apart after a save.
 */
export function useClinicMascot(): {
    mascot: ClinicMascotIdentity
    setMascot: (next: ClinicMascotIdentity) => void
    patchMascot: (patch: Partial<ClinicMascotIdentity>) => void
    clinicId: string | undefined
} {
    const { clinic } = useClinic()
    const clinicId = clinic?.id
    const [mascot, setMascotState] = useState<ClinicMascotIdentity>(() =>
        clinicId ? loadClinicMascot(clinicId) : { ...DEFAULT_CLINIC_MASCOT },
    )

    useEffect(() => {
        if (!clinicId) {
            setMascotState({ ...DEFAULT_CLINIC_MASCOT })
            return
        }
        setMascotState(loadClinicMascot(clinicId))
    }, [clinicId])

    // Cross-instance refresh: same-tab saves dispatch a custom event,
    // other-tab saves fire the native storage event.
    useEffect(() => {
        if (!clinicId) return undefined

        const reloadIfMatch = () => {
            setMascotState(loadClinicMascot(clinicId))
        }
        const onCustom = (event: Event) => {
            const detail = (event as CustomEvent<ClinicMascotUpdatedDetail>)
                .detail
            if (!detail || detail.clinicId !== clinicId) return
            reloadIfMatch()
        }
        const onStorage = (event: StorageEvent) => {
            if (event.key && !event.key.endsWith(`:${clinicId}`)) return
            reloadIfMatch()
        }

        window.addEventListener(CLINIC_MASCOT_UPDATED_EVENT, onCustom)
        window.addEventListener('storage', onStorage)
        return () => {
            window.removeEventListener(CLINIC_MASCOT_UPDATED_EVENT, onCustom)
            window.removeEventListener('storage', onStorage)
        }
    }, [clinicId])

    const setMascot = useCallback(
        (next: ClinicMascotIdentity) => {
            setMascotState(next)
            if (clinicId) saveClinicMascot(clinicId, next)
        },
        [clinicId],
    )

    const patchMascot = useCallback(
        (patch: Partial<ClinicMascotIdentity>) => {
            setMascotState(prev => {
                const next = { ...prev, ...patch }
                if (clinicId) saveClinicMascot(clinicId, next)
                return next
            })
        },
        [clinicId],
    )

    return { mascot, setMascot, patchMascot, clinicId }
}
